import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import OtpInput from '../components/OtpInput';
import FormButton from '../components/FormButton';
import FuturaSmileIcon from '../components/icons/FuturaSmileIcon';

// Backup Codes Modal Component
const BackupCodesModal: React.FC<{
    codes: string[];
    onConfirm: () => void;
}> = ({ codes, onConfirm }) => {
    const [copied, setCopied] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    const copyToClipboard = () => {
        const text = `FuturaSmile MFA Backup Codes\n${'='.repeat(30)}\n\n${codes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\nStore these codes safely. Each code can only be used once.`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    const downloadCodes = () => {
        const text = `FuturaSmile MFA Backup Codes\n${'='.repeat(30)}\n\nGenerated: ${new Date().toISOString()}\n\n${codes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\n⚠️ IMPORTANT:\n- Store these codes in a safe place\n- Each code can only be used once\n- Generate new codes if you run out`;
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'futurasmile-backup-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Backup codes downloaded!');
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-700 shadow-2xl"
            >
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">Save Your Backup Codes</h2>
                    <p className="text-gray-400 text-sm">
                        These codes can be used if you lose access to your authenticator app. Each code works only once.
                    </p>
                </div>

                {/* Codes Grid */}
                <div className="bg-zinc-800 rounded-xl p-4 mb-4 border border-zinc-700">
                    <div className="grid grid-cols-2 gap-2">
                        {codes.map((code, index) => (
                            <div key={index} className="bg-zinc-900 rounded-lg px-3 py-2 font-mono text-sm text-teal-400 text-center">
                                {code}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mb-4">
                    <button
                        onClick={copyToClipboard}
                        className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg py-2.5 px-4 transition-colors border border-zinc-600"
                    >
                        {copied ? (
                            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        )}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                        onClick={downloadCodes}
                        className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg py-2.5 px-4 transition-colors border border-zinc-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                    </button>
                </div>

                {/* Warning */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                    <p className="text-amber-400 text-xs flex items-start gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>
                            <strong>Important:</strong> Store these codes in a secure location. You won't be able to see them again.
                        </span>
                    </p>
                </div>

                {/* Acknowledge Checkbox */}
                <label className="flex items-start gap-3 mb-4 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-0 mt-0.5"
                    />
                    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                        I have saved my backup codes in a safe place
                    </span>
                </label>

                {/* Continue Button */}
                <button
                    onClick={onConfirm}
                    disabled={!acknowledged}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${acknowledged
                            ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:shadow-lg hover:shadow-teal-500/25'
                            : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                        }`}
                >
                    Continue to Dashboard
                </button>
            </motion.div>
        </motion.div>
    );
};

const MfaSetupPage: React.FC = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { setupMFA, verifyMFA, error: authError, isLoading } = useAuth();

    const [qrCode, setQrCode] = useState<string>('');
    const [secret, setSecret] = useState<string>('');
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tempToken, setTempToken] = useState<string | null>(null);
    const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

    useEffect(() => {
        if (state?.tempToken) {
            setTempToken(state.tempToken);
            handleSetup(state.tempToken);
        } else {
            navigate('/login');
        }
    }, [state, navigate]);

    const handleSetup = async (token: string) => {
        try {
            const data = await setupMFA(token);
            setQrCode(data.qrCode);
            setSecret(data.secret);
        } catch (err: any) {
            toast.error(err.message || 'Failed to initialize MFA setup');
            navigate('/login');
        }
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationCode || verificationCode.length < 6) return;

        setIsSubmitting(true);
        try {
            if (!tempToken) throw new Error('Session expired');

            const data = await verifyMFA(verificationCode, tempToken);

            toast.success('MFA Enabled Successfully!');

            // Show backup codes modal instead of alert
            if (data.backupCodes) {
                setBackupCodes(data.backupCodes);
            } else {
                navigate('/', { replace: true });
            }
        } catch (err: any) {
            toast.error(err.message || 'Verification failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackupCodesConfirm = () => {
        setBackupCodes(null);
        navigate('/', { replace: true });
    };

    if (!tempToken) return null;

    return (
        <>
            <AnimatePresence>
                {backupCodes && (
                    <BackupCodesModal
                        codes={backupCodes}
                        onConfirm={handleBackupCodesConfirm}
                    />
                )}
            </AnimatePresence>

            <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <FuturaSmileIcon className="w-12 h-12 text-teal-400 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-white mb-2">Setup Two-Factor Authentication</h1>
                        <p className="text-gray-400">
                            Your account requires enhanced security. Please set up 2FA to continue.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-zinc-800 rounded-xl p-8 border border-zinc-700 shadow-xl"
                    >
                        {qrCode ? (
                            <div className="space-y-6">
                                <div className="flex justify-center bg-white p-4 rounded-lg">
                                    <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                                </div>

                                <div className="text-center">
                                    <p className="text-sm text-gray-400 mb-2">Scan with Google Authenticator or Authy</p>
                                    <p className="text-xs font-mono text-zinc-500 bg-zinc-900 p-2 rounded truncate">
                                        {secret}
                                    </p>
                                </div>

                                <form onSubmit={handleVerify} className="space-y-6">
                                    <div className="flex flex-col items-center gap-4">
                                        <label className="text-gray-300 text-sm font-medium">Verification Code</label>
                                        <OtpInput
                                            value={verificationCode}
                                            onChange={setVerificationCode}
                                            length={6}
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <FormButton
                                        type="submit"
                                        loading={isSubmitting}
                                        disabled={isSubmitting || verificationCode.length < 6}
                                        fullWidth
                                        variant="teal"
                                    >
                                        Verify & Enable
                                    </FormButton>
                                </form>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                <p className="mt-4 text-gray-400">Initializing setup...</p>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default MfaSetupPage;

